import { Target, Compass, Leaf, Users, ArrowUpRight } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { Overline, PageHero, PrimaryLink } from "../components/Bits";
import { IMAGES } from "../lib/site";

const VALUES = [
  { icon: Target, t: "Mission-driven", d: "We exist to turn empty and underused property into safe, stable homes for the people who need them most." },
  { icon: Compass, t: "Accountable", d: "Local teams, transparent standards and routine inspections keep us honest on every doorstep." },
  { icon: Leaf, t: "Sustainable", d: "Long-term leases and proper maintenance mean homes that last — for landlords and tenants alike." },
  { icon: Users, t: "People-first", d: "Behind every tenancy is a person. Dignity, safety and support sit at the centre of what we do." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        overline="About Insight Properties"
        titleLines={["A housing division", "with a human core."]}
        subtitle="Insight Properties is a specialist rent-to-rent and supported housing provider, working hand in hand with landlords and local authorities to create safe, stable homes across the North West."
        image={IMAGES.terracedMews}
      />

      {/* STORY */}
      <section className="container-x py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <Reveal><Overline>Our story</Overline></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-5 font-display font-bold text-3xl sm:text-4xl tracking-tight text-white">
                Built for landlords, councils and communities.
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-6 space-y-5 text-slate-300 leading-relaxed text-base sm:text-lg">
                <p>
                  Insight Properties exists to solve a growing problem: the shortage of safe,
                  quality housing for people who need support — and the risk, cost and hassle
                  landlords face trying to manage it alone.
                </p>
                <p>
                  We partner with landlords on long-term rent-to-rent agreements and work hand in
                  hand with local authorities and support providers to deliver compliant, well-run
                  accommodation — all held to a compliance-first, inspection-backed standard.
                </p>
                <p>
                  The result is simple: landlords get certainty, councils get quality, and
                  vulnerable people across the North West get somewhere to call home.
                </p>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.2} className="lg:col-span-5">
            <div className="relative rounded-3xl overflow-hidden aspect-[4/5]">
              <img src={IMAGES.interiorBright} alt="Modern managed home interior" className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/60 to-transparent" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* VALUES */}
      <section className="container-x pb-20 lg:pb-28">
        <Reveal><Overline>What we stand for</Overline></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-5 font-display font-bold text-3xl sm:text-4xl tracking-tight text-white">Our values</h2>
        </Reveal>
        <Stagger className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
          {VALUES.map((v, i) => (
            <StaggerItem key={i}>
              <div className="card-surface h-full rounded-3xl p-8 lg:p-10 hover:border-amber/30 transition-colors" data-testid={`value-${i}`}>
                <v.icon className="h-9 w-9 text-amber" />
                <h3 className="mt-5 font-display font-bold text-2xl text-white">{v.t}</h3>
                <p className="mt-3 text-slate-400 leading-relaxed">{v.d}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      {/* CTA */}
      <section className="container-x pb-28 lg:pb-40">
        <Reveal>
          <div className="rounded-[2rem] card-surface p-10 lg:p-16 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-white max-w-xl">
              Want to work with a housing partner who actually cares?
            </h2>
            <PrimaryLink to="/contact" testid="about-cta">Start a conversation <ArrowUpRight className="hidden" /></PrimaryLink>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
